<template>
  <div class="referral-page">
    <header class="workspace-header">
      <div class="workspace-heading">
        <h1 class="workspace-title">{{ $t('referral.title') }}</h1>
      </div>
      <span v-if="summary && summary.referrerEmail" class="referred-by">
        {{ $t('referral.referredBy', { email: summary.referrerEmail }) }}
      </span>
    </header>

    <div v-if="!user" class="workspace-state">
      <button type="button" class="primary-btn" @click="showLoginModal">{{ $t('navbar.login') }}</button>
    </div>

    <div v-else-if="isLoading && !summary" class="workspace-state">
      <i class="material-icons spinning">refresh</i>
    </div>

    <div v-else-if="loadError" class="workspace-state error-state">
      <span>{{ $t('referral.loadFailed') }}：{{ loadError }}</span>
      <button type="button" class="primary-btn" @click="loadSummary">{{ $t('referral.retry') }}</button>
    </div>

    <div v-else-if="summary" class="referral-body">
      <p v-if="summary.isPartner" class="partner-note">
        {{ $t('referral.partnerNote') }}
        <router-link to="/partner">{{ $t('referral.partnerLink') }}</router-link>
      </p>
      <section class="workspace-panel share-panel">
        <div class="share-row">
          <div class="share-block">
            <span class="field-label">{{ $t('referral.codeLabel') }}</span>
            <div class="control-row">
              <strong class="referral-code">{{ summary.code }}</strong>
              <button type="button" class="secondary-btn" @click="copy(summary.code)">
                <span class="material-icons-outlined">content_copy</span>
                {{ $t('referral.copyCode') }}
              </button>
            </div>
          </div>
          <div class="share-block">
            <label class="field-label" for="referral-link">{{ $t('referral.linkLabel') }}</label>
            <div class="control-row">
              <input id="referral-link" class="text-input link-input" :value="referralLink" readonly @focus="$event.target.select()">
              <button type="button" class="primary-btn" @click="copy(referralLink)">
                <span class="material-icons-outlined">link</span>
                {{ $t('referral.copyLink') }}
              </button>
            </div>
          </div>
        </div>

        <ol class="rule-steps">
          <li class="rule-step">
            <span class="step-index">01</span>
            <div class="step-copy">
              <strong>{{ $t('referral.trackSignup') }}</strong>
              <span>{{ $t('referral.trackSignupDesc') }}</span>
            </div>
          </li>
          <li class="rule-step">
            <span class="step-index">02</span>
            <div class="step-copy">
              <strong>{{ $t('referral.trackActivate') }}</strong>
              <span>{{ $t('referral.trackActivateDesc', { threshold: rules.activationThreshold }) }}</span>
            </div>
            <span class="step-reward">{{ $t('referral.trackActivateReward', { reward: rules.referrerReward, inviteeReward: rules.inviteeReward }) }}</span>
          </li>
          <li class="rule-step">
            <span class="step-index">03</span>
            <div class="step-copy">
              <strong>{{ $t('referral.trackConvert') }}</strong>
              <span>{{ $t('referral.trackConvertDesc') }}</span>
            </div>
            <span class="step-reward">{{ $t('referral.trackConvertReward', { reward: formatNumber(rules.conversionReward) }) }}</span>
          </li>
        </ol>
        <p class="rule-note">
          {{ $t('referral.monthlyLimitNote', { limit: summary.monthlyRewardLimit, count: summary.monthlyRewardedCount }) }}
        </p>
      </section>

      <form v-if="summary.canBind" class="bind-row" @submit.prevent="submitBind">
        <span class="bind-title">{{ $t('referral.bindTitle') }}</span>
        <input
          v-model="bindCode"
          class="text-input code-input"
          :placeholder="$t('referral.bindPlaceholder')"
          maxlength="6"
          autocomplete="off"
          :aria-label="$t('referral.bindTitle')"
        >
        <button type="submit" class="secondary-btn" :disabled="isBinding || !bindCode.trim()">
          {{ $t('referral.bindButton') }}
        </button>
        <span class="bind-hint">{{ $t('referral.bindHint', { days: rules.bindWindowDays }) }}</span>
        <span v-if="bindError" class="bind-error">{{ bindError }}</span>
      </form>

      <section class="reward-section">
        <header class="section-header share-header">
          <div class="share-heading">
            <h2 class="section-title">{{ $t('referral.share.title') }}</h2>
            <span v-if="shareStatus" class="section-summary">{{ $t('referral.share.summary', { reward: formatNumber(shareStatus.rules.reward), per: shareStatus.rules.perEngagement }) }}</span>
          </div>
          <div
            v-if="shareStatus"
            class="reward-range"
            role="img"
            :aria-label="$t('referral.share.rangeLabel', { reward: formatNumber(shareStatus.rules.reward) })"
          >
            <span class="reward-amount">+{{ formatNumber(shareStatus.rules.reward) }}</span>
            <span class="reward-unit">{{ $t('referral.share.rangeFrom') }}</span>
            <span class="reward-arrow" aria-hidden="true">→</span>
            <span class="reward-amount reward-infinite">+∞</span>
          </div>
        </header>

        <div class="workspace-panel">
          <div v-if="!submission || submission.status !== 'approved'" class="share-kit">
            <div class="kit-header">
              <span class="field-label">{{ $t('referral.share.kitCopyLabel') }}</span>
              <div class="kit-actions">
                <button type="button" class="secondary-btn" @click="nextTemplate">
                  <span class="material-icons-outlined">refresh</span>
                  {{ $t('referral.share.kitNext') }}
                </button>
                <button type="button" class="secondary-btn" @click="copy(currentTemplate)">
                  <span class="material-icons-outlined">content_copy</span>
                  {{ $t('referral.share.kitCopy') }}
                </button>
              </div>
            </div>
            <p class="kit-copy">{{ currentTemplate }}</p>
            <span class="kit-note">{{ $t('referral.share.kitNote') }}</span>
            <span class="field-label kit-images-label">{{ $t('referral.share.kitImagesLabel') }}</span>
            <ul class="kit-images">
              <li v-for="image in shareKitImages" :key="image.src">
                <a :href="image.src" :download="image.name" class="kit-image" :aria-label="$t('referral.share.kitSaveImage')">
                  <img :src="image.src" alt="" loading="lazy">
                  <span class="kit-image-save">
                    <span class="material-icons-outlined">download</span>
                    {{ $t('referral.share.kitSaveImage') }}
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <div v-if="!shareStatus" class="share-status">
            <i class="material-icons spinning">refresh</i>
          </div>
          <div v-else-if="submission && submission.status === 'approved'" class="share-status">
            <span class="status-chip status-converted">{{ $t('referral.share.statusApproved') }}</span>
            <span class="share-status-text">{{ $t('referral.share.approvedText', { reward: formatNumber(submission.reward) }) }}</span>
          </div>
          <div v-else-if="submission && submission.status === 'pending'" class="share-status">
            <span class="status-chip status-activated">{{ $t('referral.share.statusPending') }}</span>
            <span class="share-status-text">{{ $t('referral.share.pendingText', { date: formatDate(submission.submittedAt) }) }}</span>
            <a class="share-post-link" :href="submission.postUrl" target="_blank" rel="noopener noreferrer">{{ submission.postUrl }}</a>
          </div>
          <form v-else class="share-form" @submit.prevent="submitShare">
            <p v-if="submission && submission.status === 'rejected'" class="share-rejected">
              {{ submission.rejectReason
                ? $t('referral.share.rejectedWithReason', { reason: submission.rejectReason })
                : $t('referral.share.rejected') }}
            </p>
            <div class="control-row">
              <input
                v-model="shareUrl"
                class="text-input link-input"
                :placeholder="$t('referral.share.urlPlaceholder')"
                :aria-label="$t('referral.share.urlPlaceholder')"
                autocomplete="off"
              >
              <button type="submit" class="primary-btn" :disabled="isSubmittingShare || !shareUrl.trim()">
                {{ $t('referral.share.submit') }}
              </button>
            </div>
            <span v-if="shareError" class="bind-error">{{ shareError }}</span>
          </form>

          <ol v-if="shareStatus" class="rule-steps">
            <li class="rule-step">
              <span class="step-index">01</span>
              <div class="step-copy">
                <strong>{{ $t('referral.share.stepPost') }}</strong>
                <span>{{ $t('referral.share.stepPostDesc') }}</span>
              </div>
            </li>
            <li class="rule-step">
              <span class="step-index">02</span>
              <div class="step-copy">
                <strong>{{ $t('referral.share.stepKeep', { days: shareStatus.rules.keepDays }) }}</strong>
                <span>{{ $t('referral.share.stepKeepDesc') }}</span>
              </div>
            </li>
            <li class="rule-step">
              <span class="step-index">03</span>
              <div class="step-copy">
                <strong>{{ $t('referral.share.stepSubmit') }}</strong>
                <span>{{ $t('referral.share.stepSubmitDesc', { reward: formatNumber(shareStatus.rules.reward), per: shareStatus.rules.perEngagement }) }}</span>
              </div>
              <span class="step-reward">{{ $t('referral.share.stepReward', { reward: formatNumber(shareStatus.rules.reward) }) }}</span>
            </li>
          </ol>
        </div>
      </section>

      <section class="invite-section">
        <header class="section-header">
          <h2 class="section-title">{{ $t('referral.listTitle') }}</h2>
          <span class="section-summary">
            {{ $t('referral.statRewarded', { count: summary.rewardedInvitees }) }}
            <span aria-hidden="true">·</span>
            {{ $t('referral.statEarned', { amount: formatNumber(summary.totalEarned) }) }}
          </span>
        </header>

        <div class="workspace-panel invite-panel">
          <p v-if="!summary.invitees.length" class="invite-empty">{{ $t('referral.listEmpty') }}</p>
          <ul v-else class="invite-list">
            <li v-for="(item, index) in summary.invitees" :key="index" class="invite-row">
              <span class="invite-email">{{ item.email }}</span>
              <span class="invite-date">{{ formatDate(item.boundAt) }}</span>
              <span class="invite-status">
                <span class="status-chip" :class="`status-${item.status}`">{{ statusLabel(item.status) }}</span>
                <span v-if="item.status === 'bound'" class="status-note">
                  {{ $t('referral.progressUsed', { used: item.creditsUsed, threshold: rules.activationThreshold }) }}
                </span>
                <span v-else-if="item.capped" class="status-note">{{ $t('referral.capped') }}</span>
              </span>
              <span class="invite-reward" :class="{ muted: !item.reward }">
                {{ item.reward ? `+${formatNumber(item.reward)}` : '—' }}
              </span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import {
  bindReferralCode,
  buildReferralLink,
  fetchReferralSummary
} from '@/composables/useReferral'
import { fetchShareRewardStatus, submitShareReward } from '@/composables/useGiftReward'
import { shareImages, shareTemplates } from '@/data/shareKit'

// 每个账号上次加载到的数据；再次进入页面时先显示它，同时在后台刷新
const summaryCache = new Map()
const shareStatusCache = new Map()

export default {
  name: 'ReferralPage',
  setup() {
    const { user } = useAuth()
    const { showLoginModal } = useModals()
    return { user, showLoginModal }
  },
  data() {
    return {
      summary: summaryCache.get(this.user?.uid) || null,
      isLoading: false,
      loadError: '',
      bindCode: '',
      bindError: '',
      isBinding: false,
      shareStatus: shareStatusCache.get(this.user?.uid) || null,
      shareUrl: '',
      shareError: '',
      isSubmittingShare: false,
      templateIndex: Math.floor(Math.random() * shareTemplates[this.$i18n.locale === 'zh' ? 'zh' : 'en'].length)
    }
  },
  computed: {
    rules() {
      return this.summary.rules
    },
    shareKitLocale() {
      return this.$i18n.locale === 'zh' ? 'zh' : 'en'
    },
    currentTemplate() {
      const templates = shareTemplates[this.shareKitLocale]
      return templates[this.templateIndex % templates.length]
    },
    shareKitImages() {
      return shareImages[this.shareKitLocale]
    },
    submission() {
      return this.shareStatus?.submission
    },
    referralLink() {
      return buildReferralLink(this.summary.code)
    },
    locale() {
      return this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US'
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(value) {
        if (value) this.loadSummary()
      }
    }
  },
  methods: {
    async loadSummary() {
      this.isLoading = true
      this.loadError = ''
      try {
        const uid = this.user.uid
        const [summary, shareStatus] = await Promise.all([fetchReferralSummary(), fetchShareRewardStatus()])
        this.summary = summary
        this.shareStatus = shareStatus
        summaryCache.set(uid, summary)
        shareStatusCache.set(uid, shareStatus)
      } catch (error) {
        this.loadError = error.message
      } finally {
        this.isLoading = false
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(this.$t('referral.copied'))
      } catch {
        ElMessage.error(this.$t('referral.copyFailed'))
      }
    },
    async submitBind() {
      this.isBinding = true
      this.bindError = ''
      try {
        await bindReferralCode(this.bindCode)
        ElMessage.success(this.$t('referral.bindSuccess'))
        this.bindCode = ''
        await this.loadSummary()
      } catch (error) {
        const key = `referral.errors.${error.code}`
        this.bindError = this.$te(key) ? this.$t(key) : error.message
      } finally {
        this.isBinding = false
      }
    },
    nextTemplate() {
      const count = shareTemplates[this.shareKitLocale].length
      this.templateIndex = (this.templateIndex + 1 + Math.floor(Math.random() * (count - 1))) % count
    },
    async submitShare() {
      this.isSubmittingShare = true
      this.shareError = ''
      try {
        this.shareStatus = await submitShareReward(this.shareUrl.trim())
        shareStatusCache.set(this.user.uid, this.shareStatus)
        this.shareUrl = ''
        ElMessage.success(this.$t('referral.share.submitted'))
      } catch (error) {
        const key = `referral.share.errors.${error.code}`
        this.shareError = this.$te(key) ? this.$t(key) : error.message
      } finally {
        this.isSubmittingShare = false
      }
    },
    statusLabel(status) {
      return {
        bound: this.$t('referral.statusBound'),
        activated: this.$t('referral.statusActivated'),
        converted: this.$t('referral.statusConverted')
      }[status]
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString(this.locale, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString(this.locale)
    }
  }
}
</script>

<style scoped>
.referral-page {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.partner-note {
  margin: 0 0 var(--space-4);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.partner-note a {
  color: var(--workspace-accent-dark);
  font-weight: 600;
}

.referred-by {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.referral-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

/* 分享 + 规则 */
.share-row {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: var(--space-8);
  padding: var(--space-6);
}

.share-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.field-label {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.control-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: 40px;
}

.referral-code {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-2xl);
  font-weight: 600;
  letter-spacing: 0.16em;
  line-height: 1;
}

.rule-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border-subtle);
  list-style: none;
}

.rule-step {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: auto auto;
  column-gap: var(--space-3);
  row-gap: var(--space-2);
  padding: var(--space-5) var(--space-6);
}

.rule-step + .rule-step {
  border-left: 1px solid var(--border-subtle);
}

.step-index {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  line-height: 1.7;
}

.step-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.step-copy strong {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.step-copy span {
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.step-reward {
  grid-column: 2;
  justify-self: start;
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--workspace-warm-bg);
  color: var(--color-warning-text);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 600;
  white-space: pre;
}

.rule-note {
  margin: 0;
  padding: var(--space-3) var(--space-6);
  border-top: 1px solid var(--border-subtle);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

/* 分享笔记领额度 */
.share-header {
  align-items: center;
}

.share-heading {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.reward-range {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: baseline;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--workspace-warm-bg);
}

.reward-amount {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-2xl);
  font-weight: 600;
  line-height: 1.1;
}

.reward-infinite {
  color: var(--workspace-accent-dark);
}

.reward-unit {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.reward-arrow {
  color: var(--ink-tertiary);
  font-size: var(--font-size-lg);
}

.share-kit {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--border-subtle);
}

.kit-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.kit-actions {
  display: flex;
  gap: var(--space-2);
}

.kit-copy {
  margin: 0;
  padding: var(--space-4);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.65;
  white-space: pre-line;
}

.kit-note {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.kit-images-label {
  margin-top: var(--space-3);
}

.kit-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

.kit-image {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  text-decoration: none;
}

.kit-image img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  object-position: top;
}

.kit-image-save {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--border-subtle);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.kit-image-save .material-icons-outlined {
  font-size: 16px;
}

.kit-image:hover {
  border-color: var(--border-active);
}

.kit-image:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.share-form,
.share-status {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-5) var(--space-6);
}

.share-status {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
  min-height: 40px;
}

.share-status .material-icons.spinning {
  color: var(--signal-active-strong);
  font-size: 1.25rem;
  animation: spin 1s linear infinite;
}

.share-status-text {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
}

.share-post-link {
  overflow: hidden;
  max-width: 100%;
  color: var(--workspace-accent-dark);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.share-rejected {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

/* 补填邀请码 */
.bind-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
}

.bind-title {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.bind-hint {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.bind-error {
  flex-basis: 100%;
  color: var(--color-danger);
  font-size: var(--font-size-xs);
}

/* 邀请记录 */
.section-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-3);
}

.section-title {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.section-summary {
  display: inline-flex;
  gap: var(--space-2);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.invite-empty {
  margin: 0;
  padding: var(--space-8) var(--space-6);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  text-align: center;
}

.invite-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.invite-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.4fr) 80px;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-6);
}

.invite-row + .invite-row {
  border-top: 1px solid var(--border-subtle);
}

.invite-email {
  overflow: hidden;
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invite-date {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.invite-status {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.status-chip {
  padding: 1px var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  white-space: nowrap;
}

.status-chip.status-activated {
  border-color: var(--border-active);
  background: var(--surface-active);
  color: var(--workspace-accent-dark);
}

.status-chip.status-converted {
  border-color: transparent;
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.status-note {
  overflow: hidden;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invite-reward {
  color: var(--color-success-text);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
  font-weight: 600;
  text-align: right;
}

.invite-reward.muted {
  color: var(--ink-tertiary);
  font-weight: 500;
}

/* 控件 */
.text-input {
  height: 40px;
  min-width: 0;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
}

.text-input:focus-visible {
  outline: none;
  border-color: var(--workspace-accent-dark);
  box-shadow: var(--focus-ring-brand);
}

.link-input {
  flex: 1 1 auto;
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.code-input {
  width: 150px;
  font-family: var(--font-family-data);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.code-input::placeholder {
  font-family: var(--font-family-base);
  letter-spacing: normal;
  text-transform: none;
}

.primary-btn,
.secondary-btn {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.primary-btn {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.primary-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--ink-primary) 86%, white);
}

.secondary-btn {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.secondary-btn:hover:not(:disabled) {
  border-color: var(--border-active);
  background: var(--surface-subtle);
}

.primary-btn:disabled,
.secondary-btn:disabled {
  border-color: var(--border-subtle);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.primary-btn:focus-visible,
.secondary-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.primary-btn .material-icons-outlined,
.secondary-btn .material-icons-outlined {
  font-size: 18px;
}

.error-state {
  flex-direction: column;
  color: var(--color-danger);
}

.workspace-state .material-icons.spinning {
  color: var(--signal-active-strong);
  font-size: 1.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .workspace-state .material-icons.spinning,
  .share-status .material-icons.spinning {
    animation: none;
  }
}

@media (max-width: 900px) {
  .share-row {
    grid-template-columns: 1fr;
    gap: var(--space-5);
  }

  .rule-steps {
    grid-template-columns: 1fr;
  }

  .rule-step + .rule-step {
    border-top: 1px solid var(--border-subtle);
    border-left: 0;
  }

  .invite-row {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: var(--space-1);
  }

  .invite-date {
    grid-row: 2;
  }

  .invite-status {
    grid-column: 1;
  }

  .invite-reward {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

@media (max-width: 640px) {
  .workspace-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .share-row,
  .share-kit,
  .share-form,
  .share-status,
  .rule-step,
  .invite-row {
    padding-left: var(--space-4);
    padding-right: var(--space-4);
  }

  .rule-note {
    padding: var(--space-3) var(--space-4);
  }

  .control-row {
    flex-wrap: wrap;
  }

  .link-input {
    flex-basis: 100%;
  }

  .section-header {
    flex-direction: column;
    gap: var(--space-1);
  }
}
</style>
